import CustomStore from 'devextreme/data/custom_store';
import EventBus from '@/services/event-bus';
import Api from '../../../services/Api';
import { applySamplesExportPagination } from '@/utils/samplesExport';
import {
  compareResultSortMeta,
  getResultSortMeta,
} from '@/utils/resultSort';

let lastMiningSamplesReq = null;
let lastMiningSamplesWithFiltersReq = null;
let lastMiningSampleDetailsReq = null;
let lastMiningSampleDetailsWithFiltersReq = null;

const DETAIL_SORT_SELECTOR_MAP = {
  result_value: 'value__ResultadoPresentacion',
  unit_value: 'value__unit',
  range_value: 'value__r_min',
};

const LOCAL_DETAIL_SORT_SELECTORS = [
  'result_value',
  'unit_value',
  'range_value',
  'value__ResultadoPresentacion',
  'value__unit',
  'value__r_min',
];

function normalizeDetailSortSettings(sortSettings = []) {
  return sortSettings.map((sortSetting) => ({
    ...sortSetting,
    selector:
      DETAIL_SORT_SELECTOR_MAP[sortSetting.selector] ?? sortSetting.selector,
  }));
}

function getLocalDetailSortValue(rowData, selector) {
  switch (selector) {
    case 'result_value':
    case 'value__ResultadoPresentacion':
      return getResultSortMeta(
        rowData?.value?.ResultadoPresentacion
      );
    case 'unit_value':
    case 'value__unit':
      return rowData?.value?.unit ?? '';
    case 'range_value':
    case 'value__r_min':
      return Number(rowData?.value?.r_min ?? Number.NEGATIVE_INFINITY);
    case 'parameter':
      return rowData?.parameter ?? '';
    case 'parameter_group':
      return rowData?.parameter_group ?? '';
    case 'group_order':
      return Number(rowData?.group_order ?? Number.NEGATIVE_INFINITY);
    default:
      return rowData?.[selector] ?? '';
  }
}

function sortLocalDetailRows(rows, sortSettings) {
  if (!Array.isArray(rows) || rows.length === 0 || !sortSettings?.length) {
    return rows;
  }

  const rowsCopy = [...rows];
  rowsCopy.sort((leftRow, rightRow) => {
    for (const sortSetting of sortSettings) {
      const leftValue = getLocalDetailSortValue(
        leftRow,
        sortSetting.selector
      );
      const rightValue = getLocalDetailSortValue(
        rightRow,
        sortSetting.selector
      );

      const comparison = compareResultSortMeta(
        leftValue,
        rightValue,
        sortSetting.desc
      );

      if (comparison !== 0) {
        return comparison;
      }
    }

    return 0;
  });

  return rowsCopy;
}

function normalizeMiningFilterArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function filterMiningDetailRows(rows, paramFilters = {}) {
  const selectedParameters = normalizeMiningFilterArray(
    paramFilters.parameter
  );
  const selectedParameterGroups = normalizeMiningFilterArray(
    paramFilters.parameter_group
  );
  const searchValue = String(paramFilters.filter_search || '')
    .trim()
    .toLowerCase();

  return rows.filter((row) => {
    const matchesParameter =
      selectedParameters.length === 0 ||
      selectedParameters.includes(String(row.parameter_id)) ||
      selectedParameters.includes(String(row.parameter));

    const matchesGroup =
      selectedParameterGroups.length === 0 ||
      selectedParameterGroups.includes(
        String(row.parameter_group_id)
      ) ||
      selectedParameterGroups.includes(String(row.parameter_group));

    const matchesSearch =
      !searchValue ||
      String(row.parameter || '')
        .toLowerCase()
        .includes(searchValue) ||
      String(row.parameter_group || '')
        .toLowerCase()
        .includes(searchValue) ||
      String(row.value?.ResultadoPresentacion || '')
        .toLowerCase()
        .includes(searchValue) ||
      String(row.value?.unit || '')
        .toLowerCase()
        .includes(searchValue);

    return matchesParameter && matchesGroup && matchesSearch;
  });
}

export default {
  namespaced: true,
  state: {
    allSamples: [],
    sampleDetails: null,
    isExporting: false,
  },
  actions: {
    async getSamples({ rootState }, params) {
      try {
        if (lastMiningSamplesReq && lastMiningSamplesReq.abort) {
          lastMiningSamplesReq.abort();
        }

        lastMiningSamplesReq = Api.getMiningSamples(
          params,
          rootState.contact.company,
          rootState.contact.address
        );

        const xhr = await lastMiningSamplesReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({ data: [], totalCount: 0 });
        }
        return Promise.reject(error);
      }
    },
    async getSamplesWithFilters({ rootState }, params) {
      try {
        if (
          lastMiningSamplesWithFiltersReq &&
          lastMiningSamplesWithFiltersReq.abort
        ) {
          lastMiningSamplesWithFiltersReq.abort();
        }

        lastMiningSamplesWithFiltersReq = Api.getMiningSamplesWithFilters(
          params,
          rootState.contact.company,
          rootState.contact.address
        );

        const xhr = await lastMiningSamplesWithFiltersReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({ data: [], totalCount: 0 });
        }
        return Promise.reject(error);
      }
    },
    async getSampleDetails({ rootState }, { id, params }) {
      try {
        if (lastMiningSampleDetailsReq && lastMiningSampleDetailsReq.abort) {
          lastMiningSampleDetailsReq.abort();
        }

        lastMiningSampleDetailsReq = Api.getSampleDetails(
          id,
          params,
          null,
          rootState.contact.company,
          rootState.contact.address
        );
        const xhr = await lastMiningSampleDetailsReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({
            data: [],
            totalCount: 0,
          });
        }

        return Promise.resolve({
          data: [],
          totalCount: 0,
        });
      }
    },
    async getSampleDetailsWithFilters({ rootState }, data) {
      try {
        if (
          lastMiningSampleDetailsWithFiltersReq &&
          lastMiningSampleDetailsWithFiltersReq.abort
        ) {
          lastMiningSampleDetailsWithFiltersReq.abort();
        }

        lastMiningSampleDetailsWithFiltersReq =
          Api.getSamplesDetailsWithFilters(
            data.params,
            data.id,
            rootState.contact.company,
            rootState.contact.address
          );
        const xhr = await lastMiningSampleDetailsWithFiltersReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({
            data: [],
            totalCount: 0,
          });
        }

        return Promise.resolve({
          data: [],
          totalCount: 0,
        });
      }
    },
    getStore({ dispatch, rootState }) {
      return new CustomStore({
        key: 'id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [
            { selector: 'sampling_date', desc: true },
          ];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = normalizeDetailSortSettings(loadOptions[i]);
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          params = params.slice(0, -1);
          let subsistuteParams = params.replace('skip', 'offset');
          subsistuteParams = subsistuteParams.replace(
            'take',
            'limit'
          );

          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: false,
            },
            { root: true }
          );

          subsistuteParams += sortParams;

          if (!subsistuteParams.startsWith('?')) {
            subsistuteParams = `?${subsistuteParams}`;
          }
          if (loadOptions.filter) {
            if (loadOptions.filter[0]) {
              subsistuteParams += `&filter_search=${loadOptions.filter[0].filterValue}`;
            }
          }
          return dispatch(
            'getSamples',
            applySamplesExportPagination(subsistuteParams, loadOptions)
          );
        },
      });
    },
    getDetailsStore({ dispatch, rootState, state, commit }) {
      return new CustomStore({
        key: 'parameter_id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [
            { selector: 'group_order', desc: false },
            // { selector: 'parameter_order', desc: false },
            { selector: 'parameter', desc: false },
          ];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = normalizeDetailSortSettings(loadOptions[i]);
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          params = params.slice(0, -1);
          let subsistuteParams = params.replace('skip', 'offset');
          subsistuteParams = subsistuteParams.replace(
            'take',
            'limit'
          );
          if (state.isExporting) {
            subsistuteParams += '&all=true';
          }
          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: false,
            },
            { root: true }
          );

          const hasLocalSort = sortSettings.some((sortSetting) =>
            LOCAL_DETAIL_SORT_SELECTORS.includes(sortSetting.selector)
          );

          if (!hasLocalSort) {
            subsistuteParams += sortParams;
          }

          if (!subsistuteParams.startsWith('?')) {
            subsistuteParams = `?${subsistuteParams}`;
          }
          if (
            Array.isArray(loadOptions.filter) &&
            loadOptions.filter?.[0]?.filterValue != null
          ) {
            subsistuteParams += `&filter_search=${loadOptions.filter[0].filterValue}`;
          }
          commit('setIsExporting', false);

          if (hasLocalSort) {
            const allRowsResponse = await dispatch('getSampleDetails', {
              id: rootState.filter.currentSampleId.toString(),
              params: `${subsistuteParams}${
                subsistuteParams.includes('?') ? '&' : '?'
              }all=true`,
            });

            const sortedRows = sortLocalDetailRows(
              allRowsResponse.data,
              sortSettings
            );
            const offset = loadOptions.skip ?? 0;
            const limit = loadOptions.take ?? sortedRows.length;

            return {
              data: sortedRows.slice(offset, offset + limit),
              totalCount: sortedRows.length,
            };
          }

          return dispatch('getSampleDetails', {
            id: rootState.filter.currentSampleId.toString(),
            params: subsistuteParams,
          });
        },
      });
    },
    async getStoreWithFilters({ dispatch, rootState }, paramFilters) {
      return new CustomStore({
        key: 'id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = loadOptions[i];
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          params = params.slice(0, -1);

          if (params.includes('skip')) {
            let subsistuteParams = params.replace('skip', 'offset');
            subsistuteParams = subsistuteParams.replace(
              'take',
              'limit'
            );

            if (!subsistuteParams) return [];

            const offsetLimit = subsistuteParams.split('&');

            const offset = offsetLimit[0].split('=')[1];

            const limit = offsetLimit[1].split('=')[1];
            paramFilters.offset = offset;
            paramFilters.limit = limit;
          } else {
            paramFilters.limit = null;
            paramFilters.offset = null;
          }

          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: true,
            },
            { root: true }
          );

          if (loadOptions.filter) {
            if (loadOptions.filter[0]) {
              paramFilters.filter_search =
                loadOptions.filter[0].filterValue;
            }
          } else {
            paramFilters.filter_search = null;
          }

          paramFilters.ordering = sortParams.sort;
          const requestFilters =
            loadOptions.isLoadingAll === true
              ? { ...paramFilters, offset: 0, limit: 250 }
              : paramFilters;
          return dispatch('getSamplesWithFilters', requestFilters);
        },
      });
    },
    async getStoreDetailsWithFilters(
      { dispatch },
      { id, paramFilters }
    ) {
      return new CustomStore({
        key: 'parameter_id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = loadOptions[i];
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          params = params.slice(0, -1);

          if (params.includes('skip')) {
            let subsistuteParams = params.replace('skip', 'offset');
            subsistuteParams = subsistuteParams.replace(
              'take',
              'limit'
            );

            if (!subsistuteParams) return [];

            const offsetLimit = subsistuteParams.split('&');

            const offset = offsetLimit[0].split('=')[1];

            const limit = offsetLimit[1].split('=')[1];
            paramFilters.offset = offset;
            paramFilters.limit = limit;
          } else {
            paramFilters.limit = null;
            paramFilters.offset = null;
          }

          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: true,
            },
            { root: true }
          );

          let searchValue = null;

          if (loadOptions.searchValue) {
            searchValue = loadOptions.searchValue;
          } else if (
            Array.isArray(loadOptions.filter) &&
            loadOptions.filter?.[0]?.filterValue != null
          ) {
            searchValue = loadOptions.filter[0].filterValue;
          } else if (
            Array.isArray(loadOptions.filter) &&
            typeof loadOptions.filter[2] !== 'undefined'
          ) {
            searchValue = loadOptions.filter[2];
          }

          paramFilters.filter_search = searchValue || null;

          const allRowsResponse = await dispatch('getSampleDetails', {
            id,
            params: '?all=true',
          });

          let filteredRows = filterMiningDetailRows(
            allRowsResponse.data,
            paramFilters
          );

          if (sortSettings.length) {
            filteredRows = sortLocalDetailRows(filteredRows, sortSettings);
          } else if (sortParams.sort) {
            filteredRows = sortLocalDetailRows(filteredRows, [
              { selector: 'parameter', desc: false },
            ]);
          }

          const offset = Number(paramFilters.offset ?? 0);
          const limit =
            paramFilters.limit !== null && typeof paramFilters.limit !== 'undefined'
              ? Number(paramFilters.limit)
              : filteredRows.length;

          return Promise.resolve({
            data: filteredRows.slice(offset, offset + limit),
            totalCount: filteredRows.length,
          });
        },
      });
    },
    callStoreWithFilters({ commit }, params) {
      EventBus.$emit('MiningSamplesPage', params);
    },
    callStoreDetailsWithfilters({ commit }, { id, params }) {
      EventBus.$emit('miningDetailsFilteredSamples', {
        id,
        params,
      });
    },
    setFilteredSamples({ commit }, value) {
      EventBus.$emit('MiningSamplesPage', null);
    },
    setFilteredSampleDetails({ commit }, value) {
      EventBus.$emit('miningDetailsFilteredSamples', {
        id: null,
        params: null,
      });
    },
    setSamples({ commit }, value) {
      commit('setSamples', value);
    },
    setSampleDetails({ commit }, value) {
      commit('setSampleDetails', value);
    },
    setIsExporting({ commit }, value) {
      commit('setIsExporting', value);
    },
  },
  mutations: {
    setSamples(state, value) {
      state.allSamples = value;
    },
    setSampleDetails(state, value) {
      state.sampleDetails = value;
    },
    setIsExporting(state, value) {
      state.isExporting = value;
    },
  },
};
